import PropTypes from 'prop-types';
import { Icon, Button } from '@evertrue/et-components';
import usePostmarkDomain from '../hooks/use-postmark-domain';
import useDnsDisconnectImpact from '../hooks/use-dns-disconnect-impact';
import DnsSetupForm from './DnsSetupForm';
import DnsRecordsTable from './DnsRecordsTable';
import DnsInstructions from './DnsInstructions';
import DnsManageDropdown from './DnsManageDropdown';

const STATUS_CONFIG = {
  not_configured: { label: 'Not Configured', icon: 'cancel', className: 'dns-status--not-configured' },
  pending: { label: 'Pending Verification', icon: 'access-time', className: 'dns-status--pending' },
  configured: { label: 'Verified', icon: 'check-circle', className: 'dns-status--configured' },
};

/**
 * @param {{ status: 'not_configured'|'pending'|'configured' }} props
 */
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.not_configured;
  return (
    <span className={`dns-status-badge ${config.className}`}>
      <Icon icon={config.icon} size={1} />
      {config.label}
    </span>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.oneOf(['not_configured', 'pending', 'configured']).isRequired,
};

/**
 * Main DNS configuration section. Renders the correct UI sub-state based on domain status:
 * - not_configured: dashed panel with setup button
 * - setup_form: inline DnsSetupForm for creating a domain
 * - pending: DNS records table + instructions + verify button
 * - configured: domain details + manage dropdown (disconnect only)
 */
const DnsConfigSection = () => {
  const { domain, loading, error, uiState, setUiState, status, createDomain, deleteDomain, verifyDomain } =
    usePostmarkDomain();
  const { activeCadenceCount, pendingBulkMessageCount } = useDnsDisconnectImpact(
    status === 'configured' || status === 'pending'
  );

  const dkimHost = domain ? domain.dkim_host || `pm._domainkey.${domain.name}` : '';
  const returnPathHost = domain ? domain.return_path_host || '' : '';

  const handleCreateDomain = (name, defaultEmail) => {
    createDomain(name, defaultEmail);
  };

  if (loading) {
    return (
      <div className="dns-config-section">
        <p className="dns-config-section--loading">Loading DNS configuration…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dns-config-section">
        <p className="dns-config-section--error">{error}</p>
      </div>
    );
  }

  return (
    <div className="dns-config-section">
      <div className="dns-config-section--header">
        <div className="dns-config-section--title-row">
          <h3 className="dns-config-section--title">Email Domain (DNS) Configuration</h3>
          <StatusBadge status={status} />
        </div>
        <p className="dns-config-section--description">
          Configure a custom sending domain so EverTrue can send emails on behalf of your organization. This requires
          adding two DNS records (DKIM and Return-Path) at your domain registrar.
        </p>
      </div>

      {/* Not configured — no domain set yet */}
      {status === 'not_configured' && uiState !== 'setup_form' && (
        <div className="dns-config-section--empty-panel">
          <p>No sending domain configured.</p>
          <Button type="secondary" onClick={() => setUiState('setup_form')}>
            + Set Up Domain
          </Button>
        </div>
      )}

      {/* Setup form (create) */}
      {uiState === 'setup_form' && (
        <DnsSetupForm
          onSubmit={handleCreateDomain}
          onCancel={() => setUiState('idle')}
          submitLabel="Generate DNS Records"
          isUpdate={false}
        />
      )}

      {/* Pending — records generated, awaiting DNS propagation */}
      {status === 'pending' && uiState === 'idle' && (
        <div className="dns-config-section--pending">
          <div className="dns-config-section--pending-banner">
            <Icon icon="info" size={1} />
            Add the following DNS records to your domain registrar, then click "Verify" to confirm propagation.
          </div>
          <DnsRecordsTable domain={domain} />
          <DnsInstructions dkimHost={dkimHost} returnPathHost={returnPathHost} />
          <div className="dns-config-section--verify-row">
            <Button type="default" onClick={verifyDomain}>
              Verify DNS Records
            </Button>
            <DnsManageDropdown
              onDisconnect={deleteDomain}
              domainName={domain?.name}
              activeCadenceCount={activeCadenceCount}
              pendingBulkMessageCount={pendingBulkMessageCount}
            />
          </div>
        </div>
      )}

      {/* Configured — DKIM and Return-Path verified */}
      {status === 'configured' && uiState === 'idle' && domain && (
        <div className="dns-config-section--configured">
          <div className="dns-config-section--details">
            <dl className="dns-config-section--detail-list">
              <div className="dns-config-section--detail-row">
                <dt>Domain</dt>
                <dd>{domain.name}</dd>
              </div>
              <div className="dns-config-section--detail-row">
                <dt>DKIM Verified</dt>
                <dd>{domain.dkim_verified ? 'Yes' : 'No'}</dd>
              </div>
              <div className="dns-config-section--detail-row">
                <dt>Return-Path Verified</dt>
                <dd>{domain.return_path_verified ? 'Yes' : 'No'}</dd>
              </div>
              {domain.dkim_verified_at && (
                <div className="dns-config-section--detail-row">
                  <dt>Verified At</dt>
                  <dd>{new Date(domain.dkim_verified_at).toLocaleString()}</dd>
                </div>
              )}
            </dl>
          </div>
          <div className="dns-config-section--manage-row">
            <DnsManageDropdown
              onDisconnect={deleteDomain}
              domainName={domain?.name}
              activeCadenceCount={activeCadenceCount}
              pendingBulkMessageCount={pendingBulkMessageCount}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DnsConfigSection;
